import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, ShieldCheck, Sparkles } from 'lucide-react';
import api from '../services/api';
import ProductCard from '../components/product/ProductCard';

interface Product {
  id: number;
  name: string;
  slug: string;
  price: number;
  compare_price?: number;
  featured_image: string;
  is_new?: boolean;
  is_featured?: boolean;
  colors?: any[];
  sizes?: string[];
  category?: any;
  product_type?: 'pet' | 'food' | 'accessory' | 'other';
  breed?: string;
  age?: string;
  vaccinated?: boolean;
  flavor?: string;
  weight?: string;
}

export default function HomePage() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [newArrivals, setNewArrivals] = useState<Product[]>([]);
  const [featured, setFeatured] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState('pets');
  const [tabProducts, setTabProducts] = useState<Record<string, Product[]>>({});
  const bannerRef = useRef<NodeJS.Timeout | null>(null);

  const slides = [
    { 
      image: 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&w=1600&q=80', 
      tag: 'Certified Adoptions',
      title: 'Lovable Healthy Companions', 
      subtitle: 'Find Your Perfect Pet Friend Today', 
      cta: 'Adopt A Companion', 
      link: '/category/pets' 
    },
    { 
      image: 'https://images.unsplash.com/photo-1589924691995-400dc9ecc119?auto=format&fit=crop&w=1600&q=80', 
      tag: 'Organic Feed & Nutrition',
      title: 'Premium Organic Nutrition', 
      subtitle: 'Wholesome feeding plans for dogs, cats, and birds', 
      cta: 'Shop Diets', 
      link: '/category/pet-foods' 
    },
    { 
      image: 'https://images.unsplash.com/photo-1596492784531-6e6eb5ea9993?auto=format&fit=crop&w=1600&q=80', 
      tag: 'Luxury Accessories',
      title: 'Premium Beds & Active Toys', 
      subtitle: 'Designed for optimal rest, exercise, and safety', 
      cta: 'Shop Accessories', 
      link: '/category/pet-accessories' 
    },
  ];

  useEffect(() => {
    api.get('/products/new-arrivals').then(r => setNewArrivals(r.data)).catch(() => {});
    api.get('/products/featured').then(r => setFeatured(r.data)).catch(() => {});

    // Tab products - load real categories
    api.get('/products?category=pets&per_page=4').then(r => setTabProducts(prev => ({ ...prev, pets: r.data.data || [] }))).catch(() => {});
    api.get('/products?category=pet-foods&per_page=4').then(r => setTabProducts(prev => ({ ...prev, foods: r.data.data || [] }))).catch(() => {});
    api.get('/products?category=pet-accessories&per_page=4').then(r => setTabProducts(prev => ({ ...prev, accessories: r.data.data || [] }))).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length > 1) {
      bannerRef.current = setInterval(() => setCurrentBanner(prev => (prev + 1) % slides.length), 6000);
      return () => { if (bannerRef.current) clearInterval(bannerRef.current); };
    }
  }, [slides.length]);

  const exploreCategories = [
    { name: "Pets for Adoption", slug: 'pets', desc: 'Vaccinated & microchipped companions', image: 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?auto=format&fit=crop&w=800&q=80' },
    { name: "Pet Foods & Feed", slug: 'pet-foods', desc: 'Wholesome organic nutrition', image: 'https://images.unsplash.com/photo-1589924691995-400dc9ecc119?auto=format&fit=crop&w=800&q=80' },
    { name: "Toys & Accessories", slug: 'pet-accessories', desc: 'Handcrafted collars, beds & wands', image: 'https://images.unsplash.com/photo-1541599540903-216a46ca1ad0?auto=format&fit=crop&w=800&q=80' },
  ];

  const tabs = [
    { key: 'pets', label: "Adoptable Pets" },
    { key: 'foods', label: "Premium Feeds" },
    { key: 'accessories', label: 'Pet Accessories' },
  ];

  return (
    <div className="space-y-0 bg-[#FAF8F5]">
      {/* Hero Slider */}
      <section className="relative h-[65vh] md:h-[88vh] overflow-hidden">
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${i === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
          >
            <img src={slide.image} alt={slide.title || `Banner ${i + 1}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-900/60 via-primary-500/25 to-transparent z-10" />
            
            <div className="absolute inset-0 flex items-center z-20">
              <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 md:px-8">
                <div className={`max-w-xl transition-all duration-[1000ms] delay-100 ${i === currentBanner ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                  {/* Glassmorphic Content Card */}
                  <div className="glass-premium shadow-premium p-8 md:p-12 rounded-[32px] space-y-6">
                    <span className="inline-flex items-center gap-1.5 bg-accent/25 text-[#734A1B] text-[10px] font-bold tracking-[0.25em] uppercase px-3.5 py-1.5 rounded-full">
                      <Sparkles size={11} className="text-accent" /> {slide.tag}
                    </span>
                    <h1 className="text-3xl md:text-5xl font-serif font-extrabold text-primary leading-tight">
                      {slide.title}
                    </h1>
                    <p className="text-gray-650 text-xs md:text-sm font-medium leading-relaxed">
                      {slide.subtitle}
                    </p>
                    <div className="pt-2">
                      <Link
                        to={slide.link || '/'}
                        className="inline-flex items-center gap-2 bg-primary hover:bg-accent text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-all rounded-xl shadow-premium hover:-translate-y-0.5"
                      >
                        {slide.cta} <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        {slides.length > 1 && (
          <>
            <button
              aria-label="Previous banner"
              onClick={() => setCurrentBanner(prev => (prev - 1 + slides.length) % slides.length)}
              className="absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/70 backdrop-blur-md text-primary hover:bg-white hover:text-accent transition-all duration-300 rounded-full flex items-center justify-center z-30 shadow-premium active:scale-90"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              aria-label="Next banner"
              onClick={() => setCurrentBanner(prev => (prev + 1) % slides.length)}
              className="absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/70 backdrop-blur-md text-primary hover:bg-white hover:text-accent transition-all duration-300 rounded-full flex items-center justify-center z-30 shadow-premium active:scale-90"
            >
              <ChevronRight size={20} />
            </button>
            
            {/* Dots */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2.5 z-35 bg-white/30 backdrop-blur-xs px-4 py-2 rounded-full">
              {slides.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Go to banner ${i + 1}`}
                  onClick={() => setCurrentBanner(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${i === currentBanner ? 'bg-accent w-6' : 'bg-primary/40 w-2'}`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Editorial Welcome Text */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-3">
          <p className="text-[10px] font-bold text-accent uppercase tracking-[0.3em]">The Royal Pet Store Standard</p>
          <h2 className="text-2xl md:text-3xl font-serif font-extrabold text-primary">Your Premium Pet Haven & Care Center</h2>
          <div className="w-12 h-0.5 bg-accent/30 mx-auto my-3" />
          <p className="text-gray-500 text-sm leading-relaxed max-w-2xl mx-auto font-medium">
            Welcome to <strong>Royal Pet Store</strong>. We connect loving families with healthy, vet-certified pets, and offer high-quality organic pet nutrition, comfortable bedding, and premium accessories to ensure your pets live their happiest lives.
          </p>
        </div>
      </section>

      {/* Fresh Arrivals Grid */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="text-accent text-[10px] font-extrabold uppercase tracking-[0.25em]">Just Arrived</span>
          <h2 className="text-3xl md:text-4xl font-serif font-extrabold text-primary mt-1">Fresh Arrivals</h2>
          <div className="w-10 h-0.5 bg-accent/40 mx-auto mt-4 mb-3" />
          <Link to="/new-in" className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-accent font-bold uppercase tracking-wider transition-colors">
            View All Arrivals <ArrowRight size={12} />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {(newArrivals.length > 0 ? newArrivals : Array(4).fill(null)).slice(0, 8).map((product, i) =>
            product ? (
              <ProductCard key={product.id} product={product} />
            ) : (
              <div key={i} className="animate-pulse bg-white border border-gray-150/40 rounded-3xl p-4 space-y-4">
                <div className="bg-gray-100 aspect-[4/5] w-full rounded-2xl" />
                <div className="h-4 bg-gray-100 mt-3 w-3/4 rounded-full" />
                <div className="h-4 bg-gray-100 mt-2 w-1/2 rounded-full" />
              </div>
            )
          )}
        </div>
      </section>

      {/* Explore Departments - Grid Category layouts */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-accent text-[10px] font-extrabold uppercase tracking-[0.25em]">Handpicked Departments</span>
            <h2 className="text-3xl font-serif font-extrabold text-primary mt-1">Shop Our Specialized Care</h2>
            <div className="w-10 h-0.5 bg-accent/40 mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {exploreCategories.map(cat => (
              <div key={cat.slug} className="group relative bg-[#FCFAF7] rounded-[32px] overflow-hidden border border-gray-100/65 shadow-sm hover:shadow-premium hover:border-accent/15 transition-all duration-500 flex flex-col">
                <div className="relative overflow-hidden aspect-[4/5] w-full flex-shrink-0">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/10 to-transparent" />
                  
                  {/* Category Details Text */}
                  <div className="absolute bottom-6 left-6 right-6 text-white z-10 space-y-1">
                    <h3 className="text-xl font-serif font-extrabold">{cat.name}</h3>
                    <p className="text-[11px] text-white/75 font-medium">{cat.desc}</p>
                  </div>
                </div>
                <div className="p-6 bg-white text-center">
                  <Link to={`/category/${cat.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 bg-primary group-hover:bg-accent text-white py-3.5 text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md">
                    Shop Department <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Magazine Split Banner */}
      <section className="py-24 relative overflow-hidden bg-primary-900 text-white rounded-[40px] mx-4 md:mx-8 shadow-premium">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1600&q=80" alt="Happy pets" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900 via-primary-500/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 py-10 z-10">
          <div className="max-w-xl space-y-6">
            <span className="inline-flex items-center gap-1 bg-accent text-white text-[9px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full">
              <ShieldCheck size={11} /> Vet Verified Clearance
            </span>
            <h2 className="text-4xl md:text-6xl font-serif font-extrabold text-white leading-tight">
              HAPPY &<br/><span className="text-accent">HEALTHY</span>
            </h2>
            <p className="text-gray-300 text-sm leading-relaxed font-medium">
              Bring home a bundle of pure joy. Explore our vet-certified, dewormed, and microchipped pets ready for adoption. All shipping is done via safe, climate-controlled, customized vehicles.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link to="/new-in" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/80 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-all rounded-xl shadow-md">
                Explore New Friends <ArrowRight size={14} />
              </Link>
              <Link to="/adoption-guide" className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white text-white px-7 py-3.5 text-xs font-bold uppercase tracking-widest transition-all rounded-xl">
                Adoption Guide
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Curated Essentials - Tabbed Grid */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="text-accent text-[10px] font-extrabold uppercase tracking-[0.25em]">Curated Essentials</span>
          <h2 className="text-3xl font-serif font-extrabold text-primary mt-1">Recommended for You</h2>
          <div className="w-10 h-0.5 bg-accent/40 mx-auto mt-4 mb-8" />
          
          {/* Custom Pill Switcher */}
          <div className="flex items-center justify-center gap-2 bg-[#FCFAF7] border border-gray-100 p-1.5 rounded-2xl max-w-md mx-auto flex-wrap">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`text-[11px] font-bold uppercase tracking-wider px-5 py-2.5 rounded-xl transition-all duration-300 ${activeTab === tab.key ? 'bg-primary text-white shadow-md' : 'text-gray-500 hover:text-primary'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {(tabProducts[activeTab] || []).slice(0, 4).map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
            {(tabProducts[activeTab] || []).length === 0 && Array(4).fill(null).map((_, i) => (
              <div key={i} className="animate-pulse bg-white border border-gray-150/40 rounded-3xl p-4 space-y-4">
                <div className="bg-gray-100 aspect-[4/5] w-full rounded-2xl" />
                <div className="h-4 bg-gray-100 mt-3 w-3/4 rounded-full" />
                <div className="h-4 bg-gray-100 mt-2 w-1/2 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Items Grid Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-accent text-[10px] font-extrabold uppercase tracking-[0.25em]">Special items</span>
            <h2 className="text-3xl font-serif font-extrabold text-primary mt-1">Featured Items</h2>
            <div className="w-10 h-0.5 bg-accent/40 mx-auto mt-4" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {featured.slice(0, 8).map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/search" className="btn-outline">
              Show More Catalogues
            </Link>
          </div>
        </div>
      </section>

      {/* Modern Features Trust Strip */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: '🐶', title: 'Certified Health Guarantee', desc: 'Dewormed, vaccinated and microchipped pets' },
              { icon: '🛡️', title: 'Vet Clearance Certificates', desc: 'Veterinary reports and complete lineage charts' },
              { icon: '🚚', title: 'Safe Climate Transport', desc: 'Secure temperature-regulated transport services' },
              { icon: '💬', title: '24/7 Expert Support Helpline', desc: 'Phone / WhatsApp: 0329 5025172' },
            ].map((feature, i) => (
              <div key={i} className="flex gap-4 items-start bg-[#FCFAF7] border border-gray-100/70 p-5 rounded-2xl transition-all duration-300 hover:shadow-premium">
                <div className="text-3xl p-2.5 bg-white shadow-sm rounded-xl flex-shrink-0">{feature.icon}</div>
                <div>
                  <h4 className="font-bold text-primary text-xs uppercase tracking-wider leading-snug">{feature.title}</h4>
                  <p className="text-gray-400 text-[11px] mt-1 font-semibold leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
